import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { getManufacturesListingPaginate } from "../../../api/context/manufacturer";

const ORDER_TYPE_OPTIONS = [
  { id: null, name: "NEW" },
  { id: 1, name: "RETURN" },
  { id: 2, name: "EXCHANGE" },
];

const OrderFilterModal = ({ show, onHide, onApplyFilters, searchFormik, filterData, userType }) => {
  const [activeFilter, setActiveFilter] = useState("Order Status");
  const isAdmin = userType === 'ADMIN';

  const [searchQueries, setSearchQueries] = useState({
    "Order Status": "",
    "Order Type": "",
    "Payment Mode": "",
    "Payment Status": "",
    Vendors: "",
  });

  const [localSelections, setLocalSelections] = useState({
    orderStatus: [],
    orderType: [],
    paymentMode: [],
    paymentStatus: [],
    vendors: [],
    startDate: "",
    endDate: "",
  });

  const [vendorList, setVendorList] = useState([]);

  const orderStatusOptions = [
    { id: "INITIATED", name: "INITIATED" },
    { id: "CONFIRMED", name: "CONFIRMED" },
    { id: "DELIVERED", name: "DELIVERED" },
    { id: "CANCELLED", name: "CANCELLED" },
    { id: "REJECTED", name: "REJECTED" },
  ];

  const paymentModeOptions = [
    { id: "COD", name: "COD" },
    { id: "ONLINE", name: "ONLINE" },
  ];

  const paymentStatusOptions = [
    { id: "PENDING", name: "PENDING" },
    { id: "DONE", name: "DONE" },
  ];

  const filterTypes = [
    { key: "Order Status", stateKey: "orderStatus" },
    { key: "Order Type", stateKey: "orderType" },
    { key: "Payment Mode", stateKey: "paymentMode" },
    { key: "Payment Status", stateKey: "paymentStatus" },
    ...(isAdmin ? [{ key: "Vendors", stateKey: "vendors" }] : []),
    { key: "Date Range", stateKey: "date" },
  ];

  useEffect(() => {
    if (show) {
      if (isAdmin) {
        fetchVendors();
      }

      const getOrderTypeOption = (orderType) =>
        ORDER_TYPE_OPTIONS.find((option) => option.id === orderType) || null;

      const selectedOrderType =
        filterData?.selected_orderType?.value !== undefined
          ? getOrderTypeOption(filterData.selected_orderType.value)
          : getOrderTypeOption(filterData?.orderType);

      const hasSelectedOrderType =
        Boolean(filterData?.selected_orderType) ||
        (filterData?.orderType !== null && filterData?.orderType !== undefined && filterData?.orderType !== "");

      setLocalSelections({
        orderStatus: filterData?.orderStatus
          ? [{ id: filterData.orderStatus, name: filterData.orderStatus }]
          : [],
        orderType: hasSelectedOrderType && selectedOrderType ? [selectedOrderType] : [],
        paymentMode: filterData?.paymentMode
          ? [{ id: filterData.paymentMode, name: filterData.paymentMode }]
          : [],
        paymentStatus: filterData?.paymentStatus
          ? [{ id: filterData.paymentStatus, name: filterData.paymentStatus }]
          : [],
        vendors: (filterData?.vendors || []).map((id) => ({ id })),
        startDate: filterData?.startDate || "",
        endDate: filterData?.endDate || "",
      });
    }
  }, [show, filterData, isAdmin]);

  const fetchVendors = () => {
    getManufacturesListingPaginate({ page: 1, limit: 100, search: "" })((res) => {
      console.log("Vendor API Response:", res); // 🔍 DEBUG: Check API response
      if (res?.statusCode === 200 && res?.results) {
        const vendors = res.results.map((v) => ({
          id: v.value || v.id,
          name: v.label || v.storeName
        }));
        console.log("Mapped Vendors:", vendors); // 🔍 DEBUG: Check mapped data
        setVendorList(vendors);
      } else {
        console.log("Vendor API Error or Empty:", res);
        setVendorList([]);
      }
    });
  };

  // Single selection for Order Status, Order Type, Payment Mode, Payment Status
  const singleSelectFilters = ["Order Status", "Order Type", "Payment Mode", "Payment Status"];
  const isMultiSelect = !singleSelectFilters.includes(activeFilter);

  const handleSelection = (key, item) => {
    if (isMultiSelect) {
      // Multiple selection (checkbox) - toggle behavior
      const exists = localSelections[key].some((x) => x.id === item.id);
      setLocalSelections((prev) => ({
        ...prev,
        [key]: exists
          ? prev[key].filter((x) => x.id !== item.id)
          : [...prev[key], item],
      }));
    } else {
      // Single selection (radio) - replace or deselect when clicked again
      const exists = localSelections[key].some((x) => x.id === item.id);
      setLocalSelections((prev) => ({
        ...prev,
        [key]: exists ? [] : [item],
      }));
    }
  };

  const getList = () => {
    switch (activeFilter) {
      case "Order Status": return orderStatusOptions;
      case "Order Type": return ORDER_TYPE_OPTIONS;
      case "Payment Mode": return paymentModeOptions;
      case "Payment Status": return paymentStatusOptions;
      case "Vendors": return vendorList;
      default: return [];
    }
  };

  const handleApply = () => {
    const selectedOrderType = localSelections.orderType[0] || null;
    const filters = {
      orderStatus: localSelections.orderStatus.map((i) => i.id)[0] || "",
      orderType: selectedOrderType ? selectedOrderType.id : null,
      selected_orderType: selectedOrderType
        ? { value: selectedOrderType.id, label: selectedOrderType.name }
        : null,
      paymentMode: localSelections.paymentMode.map((i) => i.id)[0] || "",
      paymentStatus: localSelections.paymentStatus.map((i) => i.id)[0] || "",
      startDate: localSelections.startDate,
      endDate: localSelections.endDate,
    };

    if (isAdmin) {
      filters.vendors = localSelections.vendors.map((i) => i.id);
    }

    onApplyFilters(filters);

    onHide();
  };

  const handleClear = () => {
    setLocalSelections({
      orderStatus: [],
      orderType: [],
      paymentMode: [],
      paymentStatus: [],
      vendors: [],
      startDate: "",
      endDate: "",
    });

    const clearedFilters = {
      orderStatus: "",
      orderType: null,
      selected_orderType: null,
      paymentMode: "",
      paymentStatus: "",
      startDate: "",
      endDate: "",
    };

    if (isAdmin) {
      clearedFilters.vendors = [];
    }

    onApplyFilters(clearedFilters);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="order-filter-modal">
      <style>{`
        /* ===== Desktop styling ===== */
        .order-filter-modal .modal-content {
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .order-filter-modal .modal-header {
          padding: 14px 18px !important;
          border-bottom: 1px solid #e2e8f0 !important;
        }
        .order-filter-modal .modal-title {
          font-size: 15px !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          letter-spacing: -0.01em;
        }
        .order-filter-modal .modal-header .btn-close {
          width: 12px; height: 12px;
          padding: 8px;
          background-size: 10px;
        }
        .order-filter-modal .modal-body { padding: 0 !important; }
        .order-filter-modal .modal-footer {
          padding: 10px 16px !important;
          border-top: 1px solid #e2e8f0 !important;
          gap: 8px;
        }
        .order-filter-modal .modal-footer .btn { font-size: 12px !important; }
        .order-filter-modal .modal-footer .request_btn {
          background: #008695 !important;
          border: 1px solid #008695 !important;
          color: #fff !important;
          font-weight: 600 !important;
          height: 34px;
          padding: 6px 18px !important;
          border-radius: 4px !important;
          font-size: 12px !important;
        }
        .order-filter-modal .modal-footer .request_btn:hover {
          background: #006672 !important;
          border-color: #006672 !important;
        }
        .order-filter-modal .btn-link {
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #64748b !important;
          text-decoration: none !important;
        }
        .order-filter-modal .btn-link:hover { color: #0f172a !important; }

        /* Fixed-height body, internal scroll */
        .order-filter-modal .filter-body-wrap {
          display: flex;
          width: 100%;
          height: 440px;
          max-height: 60vh;
          min-height: 0;
        }

        /* Left panel */
        .order-filter-modal .filter-left-panel {
          width: 200px;
          flex-shrink: 0;
          background: #f8fafc;
          border-right: 1px solid #e2e8f0;
          overflow-y: auto;
          min-height: 0;
        }
        .order-filter-modal .filter-cat {
          padding: 10px 14px;
          font-size: 12px;
          color: #475569;
          cursor: pointer;
          border-bottom: 1px solid #eef2f7;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
        }
        .order-filter-modal .filter-cat:hover { background: #eef2f7; color: #0f172a; }
        .order-filter-modal .filter-cat.active {
          background: #fff;
          color: #008695;
          font-weight: 600;
          box-shadow: inset 3px 0 0 #008695;
        }
        .order-filter-modal .filter-cat-count {
          font-size: 10px;
          background: #008695;
          color: #fff;
          padding: 1px 6px;
          border-radius: 999px;
          font-weight: 700;
        }

        /* Right panel */
        .order-filter-modal .filter-right-panel {
          flex: 1;
          padding: 16px 18px;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .order-filter-modal .filter-right-title {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 10px;
          letter-spacing: -0.01em;
        }
        .order-filter-modal .filter-search-input {
          height: 34px !important;
          font-size: 12px !important;
          padding: 6px 10px !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 4px !important;
          margin-bottom: 10px !important;
        }
        .order-filter-modal .filter-search-input:focus {
          border-color: #008695 !important;
          box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
        }
        .order-filter-modal .filter-scroll-area {
          flex: 1 1 auto;
          overflow-y: auto;
          min-height: 0;
        }
        .order-filter-modal .filter-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px !important;
          border-radius: 6px;
          margin-bottom: 2px;
          cursor: pointer;
          font-size: 12px;
          color: #0f172a;
        }
        .order-filter-modal .filter-row:hover { background: #f8fafc; }
        .order-filter-modal .filter-row.checked { background: #f0fdfa; }
        .order-filter-modal .filter-row .row-check {
          width: 14px; height: 14px;
          margin: 0;
          accent-color: #008695;
          flex-shrink: 0;
        }
        .order-filter-modal .filter-row .row-label {
          flex: 1;
          min-width: 0;
          font-size: 12px;
          line-height: 1.35;
        }
        .order-filter-modal .filter-no-items {
          text-align: center;
          padding: 24px 12px;
          font-size: 12px;
          color: #94a3b8;
        }

        /* Date range panel */
        .order-filter-modal .date-range-section .form-label {
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #475569 !important;
          margin-bottom: 4px !important;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .order-filter-modal .date-range-section .form-control {
          height: 34px !important;
          font-size: 12px !important;
          padding: 6px 10px !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 4px !important;
        }

        /* ===== Mobile bottom sheet — full width, two-panel body, half/half footer ===== */
        @media (max-width: 767.98px) {
          .modal:has(.order-filter-modal) { padding: 0 !important; }

          .order-filter-modal.modal-dialog {
            margin: 0 !important;
            width: 100vw !important;
            max-width: 100vw !important;
            min-width: 100vw !important;
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            top: auto !important;
            transform: none !important;
            display: flex !important;
            flex-direction: column;
            justify-content: flex-end;
            align-items: stretch !important;
            min-height: unset !important;
          }
          .order-filter-modal .modal-content {
            width: 100% !important;
            border-radius: 16px 16px 0 0 !important;
            border: none !important;
            border-top: 1px solid #e2e8f0 !important;
            max-height: 88vh;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }
          /* Grab handle */
          .order-filter-modal .modal-content::before {
            content: "";
            display: block;
            width: 40px;
            height: 4px;
            background: #cbd5e1;
            border-radius: 999px;
            margin: 8px auto 0;
            flex-shrink: 0;
          }
          .order-filter-modal .modal-header { padding: 6px 16px 10px !important; }
          .order-filter-modal .modal-body {
            padding: 0 !important;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            width: 100%;
          }

          .order-filter-modal .filter-body-wrap {
            display: flex !important;
            flex-direction: row !important;
            width: 100% !important;
            height: 55vh !important;
            max-height: 55vh !important;
            min-height: 0;
            flex: 1 1 auto;
          }
          .order-filter-modal .filter-left-panel {
            width: 40% !important;
            max-width: none !important;
            flex-shrink: 0;
          }
          .order-filter-modal .filter-cat {
            padding: 10px 12px;
            font-size: 11px;
            gap: 4px;
          }
          .order-filter-modal .filter-cat-count {
            font-size: 9px;
            padding: 1px 5px;
          }
          .order-filter-modal .filter-right-panel {
            padding: 12px 14px 14px;
            min-width: 0;
            flex: 1 1 auto;
            width: 60%;
          }
          .order-filter-modal .filter-right-title { font-size: 12px; margin-bottom: 8px; }
          .order-filter-modal .filter-search-input {
            height: 32px !important;
            font-size: 11px !important;
          }
          .order-filter-modal .filter-row {
            padding: 8px 8px !important;
            gap: 8px;
            font-size: 11px;
          }
          .order-filter-modal .filter-row .row-label { font-size: 11px; }

          /* Footer — two half-half buttons pinned at the bottom */
          .order-filter-modal .modal-footer {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            justify-content: stretch !important;
            gap: 10px !important;
            padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
            border-top: 1px solid #e2e8f0 !important;
            background: #fff;
            flex-shrink: 0;
            width: 100%;
          }
          .order-filter-modal .modal-footer > * {
            margin: 0 !important;
            flex: 1 1 50% !important;
            width: 50% !important;
            max-width: 50% !important;
          }
          .order-filter-modal .modal-footer .btn-link {
            height: 42px;
            border: 1px solid #cbd5e1 !important;
            border-radius: 6px !important;
            background: #fff !important;
            color: #0f172a !important;
            font-weight: 600 !important;
            font-size: 13px !important;
            display: flex !important;
            align-items: center;
            justify-content: center;
          }
          .order-filter-modal .modal-footer .request_btn {
            height: 42px !important;
            border-radius: 6px !important;
            font-size: 13px !important;
            padding: 0 !important;
            display: flex !important;
            align-items: center;
            justify-content: center;
          }
        }
      `}</style>

      <Modal.Header closeButton>
        <Modal.Title>Filters</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="filter-body-wrap">
          <div className="filter-left-panel">
            {filterTypes.map((f) => {
              const count = localSelections[f.stateKey]?.length || 0;
              const isActive = activeFilter === f.key;
              return (
                <div
                  key={f.key}
                  className={`filter-cat ${isActive ? "active" : ""}`}
                  onClick={() => setActiveFilter(f.key)}
                >
                  <span>{f.key}</span>
                  {count > 0 && <span className="filter-cat-count">{count}</span>}
                </div>
              );
            })}
          </div>

          <div className="filter-right-panel">
            <h6 className="filter-right-title">{activeFilter}</h6>

            {activeFilter === "Date Range" ? (
              <div className="date-range-section">
                <div className="mb-3">
                  <label className="form-label">Start Date</label>
                  <Form.Control
                    type="date"
                    value={localSelections.startDate}
                    max={localSelections.endDate || undefined}
                    onChange={(e) =>
                      setLocalSelections((p) => ({ ...p, startDate: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="form-label">End Date</label>
                  <Form.Control
                    type="date"
                    value={localSelections.endDate}
                    min={localSelections.startDate || undefined}
                    onChange={(e) =>
                      setLocalSelections((p) => ({ ...p, endDate: e.target.value }))
                    }
                  />
                </div>
              </div>
            ) : (
              <>
                <Form.Control
                  placeholder={`Search ${activeFilter.toLowerCase()}...`}
                  className="filter-search-input"
                  value={searchQueries[activeFilter]}
                  onChange={(e) =>
                    setSearchQueries((p) => ({ ...p, [activeFilter]: e.target.value }))
                  }
                />

                <div className="filter-scroll-area">
                  {(() => {
                    const list = getList().filter((i) =>
                      i.name.toLowerCase().includes(searchQueries[activeFilter].toLowerCase())
                    );
                    if (list.length === 0) {
                      return (
                        <div className="filter-no-items">
                          No items found{searchQueries[activeFilter] ? ` for "${searchQueries[activeFilter]}"` : ""}
                        </div>
                      );
                    }
                    return list.map((item) => {
                      const key = filterTypes.find((f) => f.key === activeFilter).stateKey;
                      const checked = localSelections[key].some((x) => x.id === item.id);
                      return (
                        <div
                          key={`${key}-${item.id ?? item.name}`}
                          onClick={() => handleSelection(key, item)}
                          className={`filter-row ${checked ? "checked" : ""}`}
                        >
                          <input
                            type={isMultiSelect ? "checkbox" : "radio"}
                            name={isMultiSelect ? "" : key}
                            className="row-check"
                            checked={checked}
                            readOnly
                          />
                          <div className="row-label">{item.name}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" onClick={handleClear}>Clear All</Button>
        <Button className="request_btn" onClick={handleApply}>Apply Filters</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderFilterModal;
